import React, { useState } from 'react';
import { toast } from 'sonner';
import { desktopApi } from '../lib/desktopApi';
import { localCleanupAfterDraftDelete } from '../lib/draftDelete.mjs';
import { deleteLocalDraft, deleteReportPhoto, listReportPhotos } from '../lib/offlineStore';
import { reconcileTransferredDraft } from '../lib/syncEngine';
import { ownershipFromConflict } from '../lib/draftTransfer.mjs';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

function clearSessionDraftUi(agentId) {
  if (!agentId || typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(`vatelsec-draft-ui:${agentId}`);
  } catch (error) {
    void error;
  }
}

async function clearLocalDraftCopy(agentId, reportId) {
  const queued = await listReportPhotos(agentId).catch(() => []);
  const plan = localCleanupAfterDraftDelete(queued, reportId);
  if (plan.clearLocalDraft) await deleteLocalDraft(agentId).catch(() => null);
  clearSessionDraftUi(agentId);
  for (const photoId of plan.dropPhotoIds) {
    await deleteReportPhoto(photoId).catch(() => null);
  }
}

const DeleteDraftButton = ({
  agentId,
  reportId = '',
  className = '',
  onBeforeDelete,
  onDeleted,
  onFailed,
}) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const confirmDelete = async () => {
    if (!agentId || busy) return;
    setBusy(true);
    onBeforeDelete?.();
    try {
      try {
        await desktopApi.deleteReportDraft(agentId, { abandon: true, reportId });
      } catch (error) {
        if (error?.response?.status === 403 || error?.response?.status === 409) {
          // Rapport passé à un collègue : on ne retire que la copie de cet appareil.
          const moved = await reconcileTransferredDraft(
            agentId,
            ownershipFromConflict(error?.response?.data?.detail),
          );
          if (moved) {
            clearSessionDraftUi(agentId);
            toast.success(moved.message);
            setOpen(false);
            onDeleted?.();
            return;
          }
        }
        if (error?.response?.status !== 404) throw error;
      }
      await clearLocalDraftCopy(agentId, reportId);
      toast.success('Brouillon supprimé');
      setOpen(false);
      onDeleted?.();
    } catch (error) {
      onFailed?.();
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' && detail.trim()
        ? detail
        : 'Impossible de supprimer le brouillon');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="btn-delete-draft"
        className={className}
      >
        Supprimer le brouillon
      </button>
      <AlertDialog open={open} onOpenChange={(next) => { if (!busy) setOpen(next); }}>
        <AlertDialogContent
          data-testid="dialog-delete-draft"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            document.querySelector('[data-testid="btn-cancel-delete-draft"]')?.focus();
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le brouillon</AlertDialogTitle>
            <AlertDialogDescription>
              Le brouillon sera effacé de cet appareil et du serveur. Cette action est irréversible.
              Un rapport déjà enregistré reste dans l'historique, avec ses contrôles, ses incidents et ses photos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="btn-cancel-delete-draft" autoFocus disabled={busy}>
              Annuler
            </AlertDialogCancel>
            <button
              type="button"
              data-testid="btn-confirm-delete-draft"
              disabled={busy}
              onClick={confirmDelete}
              className="inline-flex h-10 items-center justify-center rounded-md bg-destructive px-4 text-sm font-semibold text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
            >
              {busy ? 'Suppression...' : 'Supprimer le brouillon'}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default DeleteDraftButton;
